import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Badge, Box, Button, IconButton } from '@mui/material';
import LoopIcon from '@mui/icons-material/Loop';
import CollectionsIcon from '@mui/icons-material/Collections';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import { ArrowBackIosNew } from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import { MobilePhotoDTO } from '../Photos/MobilePhotosScreen';
import EXIF from 'exif-js';
import { enqueueSnackbar } from 'notistack';

export interface IMobileCameraProps {
    allowUpload?: boolean;
    allowMultiple?: boolean;
    defaultCameraType?: "user" | "environment";
    onBackButtonClicked: () => void;
    onConfirm(photo: MobilePhotoDTO[]): void;
}

export default function MobileCamera(props: IMobileCameraProps) {
    const { allowUpload, allowMultiple, defaultCameraType, onBackButtonClicked, onConfirm } = props;
    const { setMenuHidden, diagnosticAlerts } = useEmployeeContext();
    const webcamRef = useRef<Webcam>(null);
    const [rotate, setRotate] = useState(false);
    const [cameraType, setCameraType] = useState<"user" | "environment">(defaultCameraType ?? 'user');
    const [showPreview, setShowPreview] = useState(false);
    const [photos, setPhotos] = useState<MobilePhotoDTO[]>([]);
    const [isMirrored, setIsMirrored] = useState(defaultCameraType === "user");

    window.sendBase64Image = (base64: string) => {
        const takenDate = new Date();
        // if (diagnosticAlerts) enqueueSnackbar('Image' + base64, { variant: 'info' });
        // if (allowMultiple === true) {
        //     setPhotos((prevPhotos) => [
        //         ...prevPhotos,
        //         { image: base64, taken: takenDate }
        //     ]);
        // } else {
        onConfirm([{ image: base64, taken: takenDate }]);
        setMenuHidden(false);
        // }
    };

    window.confirmPhotos = (photos: MobilePhotoDTO[] | string) => {
        let parsedPhotos: MobilePhotoDTO[];
        if (diagnosticAlerts) enqueueSnackbar('Photos' + photos, { variant: 'info' });

        // If native sends a JSON string, parse it.
        if (typeof photos === 'string') {
            try {
                parsedPhotos = JSON.parse(photos);
            } catch (error) {
                console.error("Error parsing photos from native:", error);
                return;
            }
        } else {
            parsedPhotos = photos;
        }

        // Convert taken (if provided) to Date objects.
        parsedPhotos = parsedPhotos.map((photo) => ({
            ...photo,
            taken: photo.taken ? new Date(photo.taken) : null,
        }));

        console.log("Received photos from native:", parsedPhotos);
        // Call the onConfirm callback with the array of photos.
        onConfirm(parsedPhotos);
    };


    window.closeCamera = () => {
        setMenuHidden(false);
        onBackButtonClicked();
    }

    useEffect(() => {
        //@ts-ignore
        if (!window.ReactNativeWebView)
            setMenuHidden(true);
    }, [setMenuHidden]);

    const capture = () => {

        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            const takenDate = new Date();
            if (allowMultiple === true) {
                setPhotos((prevPhotos) => [
                    ...prevPhotos,
                    { image: imageSrc, taken: takenDate }
                ]);
            } else {
                setShowPreview(true);
                setPhotos([{ image: imageSrc, taken: takenDate }]);
            }
        }
    };

    const handleConfirm = useCallback(() => {
        if (photos) {
            onConfirm(photos);
        }
        setMenuHidden(false);
    }, [onConfirm, photos, setMenuHidden]);

    const toggleCamera = () => {
        setRotate(!rotate);
        setCameraType((prevType) => {
            const newType = prevType === "user" ? "environment" : "user";
            setTimeout(() => setIsMirrored(newType !== "user"), 100);
            return newType;
        });
    };

    const openGallery = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/jpeg, image/jpg';
        input.multiple = allowMultiple === true;

        input.onchange = async (e: Event) => {
            const files = (e.target as HTMLInputElement)?.files;
            if (files) {
                try {
                    const readers = Array.from(files).map((file) => {
                        return new Promise<MobilePhotoDTO>((resolve, reject) => {
                            const reader = new FileReader();

                            reader.onloadend = () => {
                                if (reader.result) {
                                    const binary = reader.result;

                                    EXIF.getData(file as any, function (this: any) {
                                        const exifData = EXIF.getAllTags(this);
                                        const takenDate: Date = exifData.DateTimeOriginal
                                            ? new Date(exifData.DateTimeOriginal.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3').replace(' ', 'T'))
                                            : new Date();
                                        resolve({
                                            image: binary as string,
                                            taken: takenDate ?? new Date(),
                                        });
                                    });
                                } else {
                                    reject(new Error("Failed to read file"));
                                }
                            };

                            reader.onerror = () => reject(new Error("FileReader error"));

                            reader.readAsDataURL(file);
                        });
                    });

                    const newPhotos = await Promise.all(readers);

                    setPhotos(newPhotos);
                    onConfirm(newPhotos);
                    setMenuHidden(false);
                } catch (error) {
                    console.error("Error reading files: ", error);
                }
            }
        };

        input.click();
    };

    useEffect(() => {
        //@ts-ignore
        if (window.ReactNativeWebView && allowMultiple !== undefined && allowUpload !== undefined) {
            //@ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'open-camera',
                data: JSON.stringify({
                    allowMultiple: allowMultiple,
                    allowUpload: allowUpload,
                    defaultCameraType: defaultCameraType ?? 'user'
                })
            }));
        }
    }, [allowMultiple, allowUpload, defaultCameraType]);

    //@ts-ignore
    if (window.ReactNativeWebView) {
        return null;
    }

    return (
        <Box sx={{ height: '100%', width: '100%', backgroundColor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {showPreview ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        width: '100vw',
                        backgroundColor: 'black',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                    }}
                >
                    <img
                        alt='preview'
                        src={typeof photos === 'string' ? photos : (photos[0]?.image || '')}
                        style={{
                            objectFit: 'contain',
                            maxHeight: '100%',
                            maxWidth: '100%',
                            background: 'black',
                        }}
                    />
                </Box>
            ) : (
                <Box>
                    <Webcam
                        audio={false}
                        ref={webcamRef}
                        mirrored={cameraType === 'user'}
                        screenshotFormat="image/jpeg"
                        screenshotQuality={1}
                        minScreenshotWidth={1920}
                        minScreenshotHeight={1080}
                        videoConstraints={{
                            facingMode: cameraType,
                        }}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            background: 'black',
                        }}
                    />
                    <IconButton
                        onClick={() => {
                            setMenuHidden(false);
                            onBackButtonClicked();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 20,
                            left: 20,
                            backgroundColor: '#FFC600',
                            borderRadius: '50%',
                            width: 50,
                            height: 50,
                            zIndex: 10,
                            '&:hover': {
                                background: '#FFC600',
                            },
                        }}
                    >
                        <ArrowBackIosNew sx={{ color: 'black' }} />
                    </IconButton>
                </Box>
            )}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(128, 128, 128, 0.4)',
                    backdropFilter: 'blur(6px)',
                    borderRadius: 8,
                    zIndex: 5,
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 110,
                    width: showPreview ? 170 : 260,
                    transition: 'width 0.1s ease-in-out',
                }}
            >
                {showPreview ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-around',
                            gap: 2,
                        }}
                    >
                        <IconButton
                            onClick={() => {
                                setPhotos([]);
                                setShowPreview(false);
                            }}
                            sx={{
                                backgroundColor: 'black',
                                borderRadius: '50%',
                                color: 'white',
                                width: 60,
                                height: 60,
                                '&:hover': {
                                    background: 'black',
                                },
                            }}
                        >
                            <ReplayIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleConfirm}
                            sx={{
                                backgroundColor: '#FFC600',
                                borderRadius: '50%',
                                width: 60,
                                height: 60,
                                color: 'black',
                                '&:hover': {
                                    background: '#FFC600',
                                },
                            }}
                        >
                            <CheckIcon />
                        </IconButton>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-around',
                            gap: 2,
                        }}
                    >
                        <Badge
                            badgeContent={photos.length}
                            sx={{
                                '& .MuiBadge-badge': {
                                    backgroundColor: 'black',
                                    color: 'white',
                                    right: 6,
                                    top: 6,
                                },
                            }}
                        >
                            <IconButton
                                disabled={!allowUpload}
                                onClick={() => {
                                    if (photos.length > 0) {
                                        onConfirm(photos);
                                        setMenuHidden(false);
                                    } else {
                                        openGallery();
                                    }
                                }}
                                sx={{
                                    position: 'relative',
                                    backgroundColor: photos.length > 0 ? '#FFC600' : 'black',
                                    borderRadius: '50%',
                                    color: photos.length > 0 ? 'black' : 'white',
                                    width: 60,
                                    height: 60,
                                    overflow: 'hidden',
                                    transition: 'background-color 0.4s',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        width: '200%',
                                        height: '200%',
                                        backgroundColor: photos.length > 0 ? '#FFC600' : 'black',
                                        borderRadius: '50%',
                                        transform: photos.length > 0
                                            ? 'translate(-50%, -50%) scale(1)'
                                            : 'translate(-50%, -50%) scale(0)',
                                        transition: 'transform 0.4s linear',
                                        zIndex: 0,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        position: 'relative',
                                        zIndex: 1,
                                    },
                                    '&:disabled': {
                                        background: '#555',
                                    },
                                    '&:hover': {
                                        background: 'black',
                                    },
                                }}
                            >
                                {photos.length > 0 ? <CheckIcon /> : <CollectionsIcon />}
                            </IconButton>
                        </Badge>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: 80,
                                height: 80,
                                border: '4px solid #FFC600',
                                borderRadius: '50%',
                                backgroundColor: 'transparent',
                                position: 'relative',
                            }}
                            onClick={capture}
                        >
                            <Button
                                sx={{
                                    background: '#FFC600',
                                    borderRadius: '50%',
                                    width: '90%',
                                    height: '90%',
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:active': {
                                        transform: 'scale(0.9)',
                                    },
                                    '&:hover': {
                                        background: '#FFC600',
                                    },
                                }}
                            />
                        </Box>
                        <IconButton
                            onClick={toggleCamera}
                            sx={{
                                background: 'black',
                                borderRadius: '50%',
                                color: 'white',
                                width: 60,
                                height: 60,
                                transition: 'transform 0.3s ease',
                                transform: rotate ? 'rotate(360deg)' : 'rotate(0deg)',
                                '&:hover': {
                                    background: 'black',
                                },
                            }}
                        >
                            <LoopIcon />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
